import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../api/client';
import { useFeatures } from '../../contexts/FeatureContext';
import './ClientFeatures.css';

interface ClientFeatures {
    orderingEnabled: boolean;
    paymentEnabled: boolean;
    multiLanguageEnabled: boolean;
    brandingEnabled: boolean;
    deliveryEnabled: boolean;
    deliveryPaymentEnabled: boolean;
    deliveryGuestEnabled: boolean;
    browseMenuEnabled: boolean;
}

const FEATURE_LABELS: Record<keyof ClientFeatures, { label: string; description: string; category: string }> = {
    browseMenuEnabled: {
        label: 'Sfoglia Menu',
        description: 'Mostra il pulsante "Sfoglia Menu" nella navbar dell\'app cliente',
        category: 'Navigazione'
    },
    orderingEnabled: {
        label: 'Ordinazione Abilitata',
        description: 'Permetti ai clienti di effettuare ordini dall\'app',
        category: 'Ordini'
    },
    paymentEnabled: {
        label: 'Pagamento Online',
        description: 'Abilita il pagamento online per ordini al tavolo',
        category: 'Pagamenti'
    },
    deliveryEnabled: {
        label: 'Delivery Abilitato',
        description: 'Abilita la sezione delivery/asporto nell\'app cliente',
        category: 'Delivery'
    },
    deliveryPaymentEnabled: {
        label: 'Pagamento Delivery',
        description: 'Permetti pagamento online per ordini delivery',
        category: 'Delivery'
    },
    deliveryGuestEnabled: {
        label: 'Delivery per Ospiti',
        description: 'Permetti ordini delivery senza registrazione',
        category: 'Delivery'
    },
    multiLanguageEnabled: {
        label: 'Multi-lingua',
        description: 'Abilita la selezione della lingua nell\'app',
        category: 'Localizzazione'
    },
    brandingEnabled: {
        label: 'Branding Personalizzato',
        description: 'Mostra logo e colori personalizzati',
        category: 'Aspetto'
    },
};

export const ClientFeatures: React.FC = () => {
    const [features, setFeatures] = useState<ClientFeatures | null>(null);
    const [grants, setLocalGrants] = useState<ClientFeatures | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { refresh: refreshGlobalFeatures } = useFeatures();

    useEffect(() => {
        loadFeatures();
    }, []);

    const loadFeatures = async () => {
        try {
            console.log('=== LOADING CLIENT FEATURES ===');
            const tenantId = localStorage.getItem('tenantId');
            console.log('Current tenantId from localStorage:', tenantId);

            const response = await apiFetch('/api/tenant/self/features');

            if (response.ok) {
                const data = await response.json();
                console.log('Loaded features:', data);
                const { grants: payloadGrants, ...effective } = data || {};
                setFeatures(effective as ClientFeatures);
                // grants espone il puro stato del superadmin: se assente (BC),
                // assumiamo che ogni feature attualmente effettiva sia anche concessa.
                setLocalGrants((payloadGrants || effective) as ClientFeatures);
            } else {
                console.error('Failed to load features. Status:', response.status);
            }
        } catch (error) {
            console.error('Error loading features:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (key: keyof ClientFeatures) => {
        if (!features) return;
        // Non permettere di attivare ciò che il superadmin non ha concesso.
        if (grants && !grants[key]) return;
        setFeatures({ ...features, [key]: !features[key] });
    };

    const handleSave = async () => {
        if (!features) return;
        setSaving(true);
        try {
            console.log('=== SAVING CLIENT FEATURES ===');
            console.log('Features to save:', features);

            const response = await apiFetch('/api/tenant/self/features', {
                method: 'POST',
                body: JSON.stringify(features)
            });

            if (response.ok) {
                console.log('Features saved successfully');
                const data = await response.json().catch(() => null);
                if (data) {
                    const { grants: payloadGrants, ...effective } = data;
                    setFeatures(effective as ClientFeatures);
                    setLocalGrants((payloadGrants || effective) as ClientFeatures);
                }
                refreshGlobalFeatures();
                alert('Impostazioni salvate con successo!');
            } else {
                console.error('Failed to save features. Status:', response.status);
                alert('Errore durante il salvataggio');
            }
        } catch (error) {
            console.error('Error saving features:', error);
            alert('Errore durante il salvataggio');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="client-features-loading">Caricamento...</div>;
    }

    if (!features) {
        return <div className="client-features-error">Errore nel caricamento delle impostazioni</div>;
    }

    // Raggruppa features per categoria
    const categories = Array.from(new Set(Object.values(FEATURE_LABELS).map(f => f.category)));

    return (
        <div className="client-features">
            <div className="client-features-header">
                <h1>⚙️ Impostazioni App Cliente</h1>
                <p>Configura le funzionalità visibili nell'applicazione cliente</p>
            </div>

            <div className="client-features-content">
                {categories.map(category => (
                    <div key={category} className="feature-category">
                        <h2>{category}</h2>
                        <div className="feature-list">
                            {(Object.entries(FEATURE_LABELS) as [keyof ClientFeatures, typeof FEATURE_LABELS[keyof ClientFeatures]][])
                                .filter(([_, config]) => config.category === category)
                                .map(([key, config]) => {
                                    const isGranted = !grants || grants[key];
                                    return (
                                    <div key={key} className={`feature-item${!isGranted ? ' feature-item--locked' : ''}`}>
                                        <div className="feature-info">
                                            <h3>{config.label}{!isGranted && <span className="feature-locked-badge" title="Funzionalità non disponibile nel tuo piano. Contatta l'assistenza."> 🔒 Non disponibile</span>}</h3>
                                            <p>{config.description}</p>
                                        </div>
                                        <label className={`feature-toggle${!isGranted ? ' feature-toggle--disabled' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={features[key]}
                                                disabled={!isGranted}
                                                onChange={() => handleToggle(key)}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                    );
                                })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="client-features-actions">
                <button
                    className="btn-save"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? 'Salvataggio...' : '💾 Salva Modifiche'}
                </button>
            </div>
        </div>
    );
};

export default ClientFeatures;
